import { useState } from 'react';

import { ApiError } from '../../api/client';
import type { ChannelDto, ChannelLimits } from '../../api/types';
import { useChannel, useDeleteChannel, usePatchChannel, usePauseChannel, useResumeChannel } from '../../hooks/use-channels';
import { useSession } from '../../hooks/use-session';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';
import { ChannelLimitsForm } from './ChannelLimitsForm';
import { DeleteChannelModal } from './DeleteChannelModal';
import { PauseResumeModal } from './PauseResumeModal';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  onboarding: 'warning',
  onboarding_blocked: 'danger',
  restricted: 'danger',
  disabled: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Активен',
  onboarding: 'Прогрев',
  onboarding_blocked: 'Прогрев остановлен',
  restricted: 'На паузе',
  disabled: 'Отключён',
};

/** Short "N of M in the window" line shown under the limits cell. */
function usageLine(c: ChannelDto): string {
  const l = c.limitsNormalized;
  return `${l.window_max} сообщ. / ${l.window_hours} ч`;
}

/**
 * The limits editor for one channel. Split out so it can load that channel's
 * current window/day/week usage (only fetched while the form is actually open).
 */
function ChannelLimitsEditor({
  channel,
  onSave,
  onCancel,
  pending,
  error,
}: {
  channel: ChannelDto;
  onSave: (limits: ChannelLimits) => void;
  onCancel: () => void;
  pending: boolean;
  error: string | null;
}) {
  const detail = useChannel(channel.id);
  return (
    <ChannelLimitsForm
      limits={channel.limitsNormalized}
      usage={detail.data?.usage}
      onSave={onSave}
      onCancel={onCancel}
      pending={pending}
      error={error}
    />
  );
}

export function ChannelsList({
  channels,
  selectedId,
  onSelect,
}: {
  channels: ChannelDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [limitsError, setLimitsError] = useState<string | null>(null);
  const [pauseTarget, setPauseTarget] = useState<{ id: string; mode: 'pause' | 'resume' } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChannelDto | null>(null);
  const [blockedBy, setBlockedBy] = useState<number | null>(null);
  const patchChannel = usePatchChannel();
  const pause = usePauseChannel();
  const resume = useResumeChannel();
  const remove = useDeleteChannel();
  const { me } = useSession();
  const toast = useToast();

  function saveLimits(id: string, limits: ChannelLimits) {
    patchChannel.mutate(
      { id, body: { limits } },
      {
        onSuccess: () => {
          setEditing(null);
          setLimitsError(null);
          toast.push('Лимиты сохранены', 'success');
        },
        onError: (err) => setLimitsError(err instanceof Error ? err.message : 'Не удалось сохранить лимиты'),
      },
    );
  }

  function confirmPauseResume(reason: string) {
    if (!pauseTarget) return;
    const mutation = pauseTarget.mode === 'pause' ? pause : resume;
    mutation.mutate(
      { id: pauseTarget.id, reason },
      {
        onSuccess: () => setPauseTarget(null),
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Действие не выполнено'),
      },
    );
  }

  function confirmDelete(force: boolean) {
    if (!deleteTarget) return;
    remove.mutate(
      { id: deleteTarget.id, force },
      {
        onSuccess: () => {
          toast.push('Канал удалён', 'success');
          setDeleteTarget(null);
          setBlockedBy(null);
        },
        onError: (err) => {
          // 409: keep the dialog open and explain what to do instead.
          if (err instanceof ApiError && err.code === 'channel_has_dialogs') {
            const open = err.details.openDialogs;
            setBlockedBy(typeof open === 'number' ? open : 1);
            return;
          }
          toast.push(err instanceof Error ? err.message : 'Не удалось удалить канал');
        },
      },
    );
  }

  if (channels.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-fg">Каналы ещё не подключены</div>;
  }

  return (
    <>
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs uppercase text-muted-fg">
          <tr>
            <th className="px-3 py-2">Канал</th>
            <th className="px-3 py-2">Тип</th>
            <th className="px-3 py-2">Статус</th>
            <th className="px-3 py-2">Лимиты</th>
            <th className="px-3 py-2">Действия</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((c) => (
            <tr
              key={c.id}
              className={`cursor-pointer border-b border-border hover:bg-muted/40 ${
                selectedId === c.id ? 'bg-primary/10' : ''
              }`}
              onClick={() => onSelect(c.id)}
            >
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <Avatar name={c.title ?? '?'} photoUrl={c.photoUrl} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{c.title}</div>
                    <div className="truncate text-xs text-muted-fg">
                      {c.screenName ? `vk.com/${c.screenName}` : (c.vkGroupId ?? c.vkUserId ?? '')}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 text-xs">{c.type === 'vk_group' ? 'Сообщество' : 'Личный'}</td>
              <td className="px-3 py-2">
                <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{STATUS_LABEL[c.status] ?? c.status}</Badge>
                {c.statusReason && <div className="mt-0.5 text-xs text-muted-fg">{c.statusReason}</div>}
              </td>
              <td className="px-3 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                {editing === c.id ? (
                  <ChannelLimitsEditor
                    channel={c}
                    onSave={(limits) => saveLimits(c.id, limits)}
                    onCancel={() => {
                      setEditing(null);
                      setLimitsError(null);
                    }}
                    pending={patchChannel.isPending}
                    error={limitsError}
                  />
                ) : (
                  <div>
                    <div className="text-xs text-muted-fg">{usageLine(c)}</div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(c.id);
                        setLimitsError(null);
                      }}
                    >
                      Изменить лимиты
                    </Button>
                  </div>
                )}
              </td>
              <td className="px-3 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-wrap gap-1">
                  {c.status === 'restricted' || c.status === 'disabled' ? (
                    <Button size="sm" variant="secondary" onClick={() => setPauseTarget({ id: c.id, mode: 'resume' })}>
                      Возобновить
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setPauseTarget({ id: c.id, mode: 'pause' })}>
                      Пауза
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      setDeleteTarget(c);
                      setBlockedBy(null);
                    }}
                  >
                    Удалить
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PauseResumeModal
        mode={pauseTarget?.mode ?? null}
        onCancel={() => setPauseTarget(null)}
        onConfirm={confirmPauseResume}
        pending={pause.isPending || resume.isPending}
      />
      <DeleteChannelModal
        channel={deleteTarget}
        blockedBy={blockedBy}
        canForce={me?.role === 'owner'}
        pending={remove.isPending}
        onCancel={() => {
          setDeleteTarget(null);
          setBlockedBy(null);
        }}
        onConfirm={confirmDelete}
      />
    </>
  );
}
